/**
 * 360° Video Viewer Component
 * 
 * A custom web component that enables drag-to-scrub interaction on videos,
 * mimicking 3D model viewer behavior for product showcase videos.
 * 
 * Features:
 * - Drag to scrub through video frames (horizontal gesture)
 * - Auto-rotate animation on first load
 * - Visual hint overlay to indicate interactivity
 * - Touch and pointer event support
 * - Configurable sensitivity for desktop vs mobile
 * - Fallback to standard video controls for older browsers
 * 
 * Usage:
 * <video-360-viewer data-autorotate="true" data-sensitivity="0.015">
 *   <video src="..." muted playsinline></video>
 * </video-360-viewer>
 * 
 * Detection: Videos with alt text containing "360" are wrapped in this component.
 */

if (!customElements.get('video-360-viewer')) {
  class Video360Viewer extends HTMLElement {
    constructor() {
      super();
      this.video = null;
      this.isDragging = false;
      this.lastX = 0;
      this.hasInteracted = false;
      this.hintTimeout = null;
      this.autorotateComplete = false;
      
      // Sensitivity: how much video time changes per pixel of drag
      // Lower = more precision, Higher = faster scrubbing
      this.sensitivity = 0.015;
      this.touchSensitivity = 0.008; // Slower for touch (larger movements)
      
      // Bound methods for event listener cleanup
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onPointerCancel = this.handlePointerUp.bind(this);
    }
    
    connectedCallback() {
      // Check for PointerEvent support - fallback to standard controls if not available
      if (!window.PointerEvent) {
        this.enableFallbackMode();
        return;
      }
      
      this.video = this.querySelector('video');
      if (!this.video) return;
      
      // Parse custom sensitivity from data attributes
      if (this.dataset.sensitivity) {
        this.sensitivity = parseFloat(this.dataset.sensitivity);
      }
      if (this.dataset.touchSensitivity) {
        this.touchSensitivity = parseFloat(this.dataset.touchSensitivity);
      }
      
      // Configure video for 360° viewing
      this.video.controls = false;
      this.video.muted = true;
      this.video.loop = false; // We control looping manually for scrubbing
      this.video.playsInline = true;
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('webkit-playsinline', '');
      this.video.preload = 'auto'; // Need frames loaded for scrubbing
      
      // Add component class for styling
      this.classList.add('video-360-viewer');
      
      // Create hint overlay
      this.createHintOverlay();
      
      // Bind pointer events
      this.addEventListener('pointerdown', this.onPointerDown);
      
      // Prevent context menu on long press (mobile)
      this.addEventListener('contextmenu', (e) => e.preventDefault());
      
      // Wait for video metadata, then autorotate if enabled
      if (this.video.readyState >= 1) {
        this.onVideoReady();
      } else {
        this.video.addEventListener('loadedmetadata', () => this.onVideoReady(), { once: true });
      }
    }
    
    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.onPointerDown);
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerCancel);
      
      if (this.hintTimeout) {
        clearTimeout(this.hintTimeout);
      }
    }
    
    /**
     * Enable fallback mode for browsers without PointerEvent support
     * Shows standard video controls instead of custom interaction
     */
    enableFallbackMode() {
      this.classList.add('video-360-viewer--fallback');
      const video = this.querySelector('video');
      if (video) {
        video.controls = true;
        video.muted = true;
      }
    }
    
    /**
     * Called when video metadata is loaded
     */
    onVideoReady() {
      // Start at first frame
      this.video.currentTime = 0;
      
      // Show hint overlay
      this.showHint();
      
      // Autorotate if enabled
      if (this.dataset.autorotate === 'true') {
        this.autorotate();
      }
    }
    
    /**
     * Create the visual hint overlay (rotating arrow icon)
     */
    createHintOverlay() {
      const hint = document.createElement('div');
      hint.className = 'video-360-hint';
      hint.innerHTML = `
        <svg class="video-360-hint__icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="22" stroke="currentColor" stroke-width="2" stroke-dasharray="4 4" opacity="0.5"/>
          <path d="M32 24C32 28.4183 28.4183 32 24 32C19.5817 32 16 28.4183 16 24C16 19.5817 19.5817 16 24 16" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
          <path d="M24 12L28 16L24 20" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8 24H12M36 24H40" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.5"/>
        </svg>
        <span class="video-360-hint__text">360°</span>
      `;
      this.hint = hint;
      this.appendChild(hint);
    }
    
    /**
     * Show the hint overlay
     */
    showHint() {
      if (this.hint && !this.hasInteracted) {
        this.hint.classList.add('video-360-hint--visible');
        
        // Auto-hide after 4 seconds
        this.hintTimeout = setTimeout(() => {
          this.hideHint();
        }, 4000);
      }
    }
    
    /**
     * Hide the hint overlay
     */
    hideHint() {
      if (this.hint) {
        this.hint.classList.remove('video-360-hint--visible');
        this.hint.classList.add('video-360-hint--hidden');
      }
    }
    
    /**
     * Play auto-rotate animation on first load
     * Plays video once through at 0.5x speed to show full rotation
     */
    autorotate() {
      if (this.autorotateComplete || this.hasInteracted) return;
      
      this.video.playbackRate = 0.5;
      this.video.play().then(() => {
        // When video ends, pause at start
        this.video.addEventListener('ended', () => {
          this.video.currentTime = 0;
          this.video.pause();
          this.video.playbackRate = 1;
          this.autorotateComplete = true;
        }, { once: true });
      }).catch(() => {
        // Autoplay blocked - that's fine, user can still drag
        this.autorotateComplete = true;
      });
    }
    
    /**
     * Handle pointer down - start drag
     */
    handlePointerDown(event) {
      // Ignore right-click
      if (event.button !== 0) return;
      
      // Stop autorotate if running
      if (!this.autorotateComplete) {
        this.video.pause();
        this.autorotateComplete = true;
      }
      
      this.isDragging = true;
      this.lastX = event.clientX;
      
      // Mark as interacted and hide hint
      if (!this.hasInteracted) {
        this.hasInteracted = true;
        this.hideHint();
        if (this.hintTimeout) {
          clearTimeout(this.hintTimeout);
        }
      }
      
      // Visual feedback
      this.classList.add('video-360-viewer--dragging');
      
      // Capture pointer for reliable tracking
      this.setPointerCapture(event.pointerId);
      
      // Listen on document for move/up (works even if pointer leaves element)
      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('pointercancel', this.onPointerCancel);
      
      // Prevent text selection while dragging
      event.preventDefault();
    }
    
    /**
     * Handle pointer move - scrub video
     */
    handlePointerMove(event) {
      if (!this.isDragging) return;
      
      const delta = event.clientX - this.lastX;
      this.lastX = event.clientX;
      
      // Skip tiny movements
      if (Math.abs(delta) < 1) return;
      
      // Use different sensitivity for touch vs mouse
      const sens = event.pointerType === 'touch' ? this.touchSensitivity : this.sensitivity;
      
      // Get current time and duration
      const duration = this.video.duration;
      if (!duration || duration <= 0 || isNaN(duration)) return;
      
      // Define safe boundaries (avoid exact 0 and end where seeking can fail)
      const minTime = 0.05;
      const maxTime = duration - 0.05;
      
      // Calculate new time (drag right = forward, drag left = backward)
      let newTime = this.video.currentTime + (delta * sens);
      
      // Clamp to safe range (no wrap - video needs proper encoding for that)
      if (newTime < minTime) newTime = minTime;
      if (newTime > maxTime) newTime = maxTime;
      
      // Apply new time
      this.video.currentTime = newTime;
    }
    
    /**
     * Handle pointer up - end drag
     */
    handlePointerUp(event) {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.classList.remove('video-360-viewer--dragging');
      
      // Release pointer capture
      if (event.pointerId !== undefined) {
        this.releasePointerCapture(event.pointerId);
      }
      
      // Remove document listeners
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerCancel);
    }
  }
  
  customElements.define('video-360-viewer', Video360Viewer);
}
